import React, { useState, useEffect } from 'react';
import { Task, TaskPriority, TaskStatus } from '../../models/orgModels';
import { validateTask } from '../../models/orgMappers';
import { companyOrgService } from '../../services/companyOrgService';
import { fileStorageService, FileInfo } from '../../services/fileStorageService';
import awsS3Service from '../../services/awsS3Service';
import DocumentViewer from '../DocumentViewer';
import BulkUpload from '../BulkUpload';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config';
import { adminWhatsAppService } from '../../services/adminWhatsAppService';

interface Props {
  folderId: string;
  fileId: string;
  onCreated?: (taskId: string) => void;
  onError?: (message: string) => void;
  onDownload?: () => void;
  taskToEdit?: any; // Task to edit
}

const statuses: TaskStatus[] = ['todo','in_progress','blocked','completed','cancelled'];
const priorities: TaskPriority[] = ['low','medium','high','urgent'];
const categories = ['magazine', 'event', 'project', 'other'];

const TaskForm: React.FC<Props> = ({ folderId, fileId, onCreated, onError, onDownload, taskToEdit }) => {
  const [form, setForm] = useState<Task>({
    task_id: '',
    title: '',
    description: '',
    assigned_to: '',
    status: 'todo',
    priority: 'medium',
    due_date: '',
    start_date: '',
    completion_date: '',
    attachments: [],
    comments: [],
    dependencies: []
  });
  const [depsText, setDepsText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<{url: string, name: string} | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<FileInfo[]>([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [completionDate, setCompletionDate] = useState<Date | null>(null);
  const [users, setUsers] = useState<Array<{id: string, name: string, email: string, phoneNumber?: string}>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [sendWhatsApp, setSendWhatsApp] = useState(true); // Default to true for automatic notifications

  const update = (k: keyof Task, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  // Initialize WhatsApp service and fetch users
  useEffect(() => {
    adminWhatsAppService.initialize();
    fetchUsers();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (taskToEdit) {
      console.log('Populating form with task data:', taskToEdit);
      setForm({
        task_id: taskToEdit.id || '',
        title: taskToEdit.title || '',
        description: taskToEdit.description || '',
        assigned_to: taskToEdit.assignedTo || '',
        status: taskToEdit.status || 'todo',
        priority: taskToEdit.priority || 'medium',
        due_date: taskToEdit.dueDate || '',
        start_date: taskToEdit.startDate || '',
        completion_date: taskToEdit.completionDate || '',
        attachments: taskToEdit.attachments || [],
        comments: taskToEdit.comments || [],
        dependencies: taskToEdit.dependencies || []
      });
      
      // Set dates
      if (taskToEdit.dueDate) {
        setDueDate(new Date(taskToEdit.dueDate));
      }
      if (taskToEdit.startDate) {
        setStartDate(new Date(taskToEdit.startDate));
      }
      if (taskToEdit.completionDate) {
        setCompletionDate(new Date(taskToEdit.completionDate));
      }
      
      // Set category
      if (taskToEdit.category) {
        setSelectedCategory(taskToEdit.category);
      }
      
      // Set dependencies text
      if (taskToEdit.dependencies && taskToEdit.dependencies.length > 0) {
        setDepsText(taskToEdit.dependencies.join(', '));
      }
    }
  }, [taskToEdit]);

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().displayName || doc.data().name || 'Unknown',
        email: doc.data().email || '',
        phoneNumber: doc.data().phoneNumber || doc.data().phone || ''
      })) as Array<{id: string, name: string, email: string, phoneNumber?: string}>;
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleDueDateChange = (date: Date | null) => {
    setDueDate(date);
    update('due_date', date ? date.toISOString().split('T')[0] : '');
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    update('start_date', date ? date.toISOString().split('T')[0] : '');
  };

  const handleCompletionDateChange = (date: Date | null) => {
    setCompletionDate(date);
    update('completion_date', date ? date.toISOString().split('T')[0] : '');
  };

  const sendWhatsAppMessage = async (taskTitle: string, assignedTo: string, dueDate: string) => {
    // Find the selected user
    const selectedUser = users.find(user => user.name === assignedTo);
    
    if (!selectedUser || !selectedUser.phoneNumber) {
      console.log('No phone number available for user:', assignedTo);
      return;
    }

    // Create notification data exactly like AdminDashboard
    const notificationData = {
      taskId: `task_${Date.now()}`, // Generate a temporary ID
      taskTitle: taskTitle,
      taskDescription: form.description || '',
      teamId: null, // Individual task
      teamName: 'Individual Task',
      assignedTo: selectedUser.id,
      assignedToName: assignedTo,
      priority: form.priority || 'medium',
      dueDate: dueDate ? new Date(dueDate) : new Date(),
      taskLink: `${window.location.origin}/task/task_${Date.now()}`,
      createdAt: new Date()
    };

    try {
      const result = await adminWhatsAppService.sendTaskCreationNotification(
        notificationData,
        selectedUser.id
      );
      
      if (result.success) {
        console.log('✅ WhatsApp message sent successfully to:', selectedUser.phoneNumber);
      } else {
        console.error('❌ Failed to send WhatsApp message:', result.message);
      }
    } catch (error) {
      console.error('❌ Error sending WhatsApp message:', error);
    }
  };

  const handleSingleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    
    try {
      setUploading(true);
      console.log('📁 Starting file upload...');
      
      let fileInfo: FileInfo;
      
      // Try AWS S3 first (most cost-effective)
      if (awsS3Service.isConfigured()) {
        try {
          console.log('🚀 Uploading to AWS S3...');
          fileInfo = await awsS3Service.uploadFile(
            file, 
            `org/${folderId}/${fileId}/attachments`,
            (progress) => {
              console.log(`AWS S3 Upload progress: ${progress.percentage}%`);
            }
          );
          console.log('✅ File uploaded to AWS S3 successfully');
        } catch (s3Error) {
          console.warn('⚠️ AWS S3 upload failed, falling back to Firestore:', s3Error);
          console.log('🔄 Using Firestore fallback...');
          fileInfo = await fileStorageService.uploadFile(
            file, 
            `org/${folderId}/${fileId}/attachments`,
            (progress) => {
              console.log(`Firestore Upload progress: ${progress.percentage}%`);
            }
          );
          console.log('✅ File uploaded to Firestore successfully (fallback)');
        }
      } else {
        console.log('⚠️ AWS S3 not configured, using Firestore fallback...');
        fileInfo = await fileStorageService.uploadFile(
          file, 
          `org/${folderId}/${fileId}/attachments`,
          (progress) => {
            console.log(`Firestore Upload progress: ${progress.percentage}%`);
          }
        );
        console.log('✅ File uploaded to Firestore successfully');
      }
      
      setAttachedFiles(prev => [...prev, fileInfo]);
      console.log('✅ File uploaded successfully:', fileInfo.url);
      
    } catch (err) {
      console.error('❌ Upload error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      onError && onError(errorMessage);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleBulkUpload = (files: FileInfo[]) => {
    setAttachedFiles(prev => [...prev, ...files]);
    setShowBulkUpload(false);
  };

  const handleSubmit = async () => {
    try {
      const id = form.task_id || `t_${Date.now()}`;
      const dependencies = depsText ? depsText.split(',').map(s=>s.trim()).filter(Boolean) : [];
      const toValidate = { 
        ...form, 
        task_id: id, 
        dependencies,
        due_date: dueDate?.toISOString().split('T')[0] || form.due_date,
        start_date: startDate?.toISOString().split('T')[0] || form.start_date,
        completion_date: completionDate?.toISOString().split('T')[0] || form.completion_date
      } as Task;
      validateTask(toValidate);
      
      const taskData = {
        folderId,
        fileId,
        title: toValidate.title,
        description: toValidate.description || '',
        assignedTo: toValidate.assigned_to,
        category: (selectedCategory || 'project') as any,
        status: toValidate.status as any,
        priority: (toValidate.priority === 'urgent' ? 'high' : toValidate.priority) as any,
        dueDate: toValidate.due_date || undefined,
        startDate: toValidate.start_date || undefined,
        completionDate: toValidate.completion_date || undefined,
        attachments: [
          ...(toValidate.attachments as any) || [],
          ...attachedFiles.map(file => ({
            id: file.id,
            name: file.name,
            url: file.url,
            mime_type: file.type,
            size_bytes: file.size
          }))
        ],
        dependencies,
        notes: toValidate.description || ''
      };

      if (taskToEdit) {
        // Update existing task
        console.log('Updating task:', taskToEdit.id, taskData);
        await companyOrgService.updateTask(taskToEdit.id!, taskData);
        onCreated && onCreated(taskToEdit.id!);
      } else {
        // Create new task
        const newId = await companyOrgService.createTask(taskData);
        
        // If a user is assigned, also create a task in the main tasks collection for notifications
        if (toValidate.assigned_to && toValidate.assigned_to.trim() !== '') {
          const selectedUser = users.find(user => user.name === toValidate.assigned_to);
          if (selectedUser) {
            try {
              const mainTaskData = {
                title: toValidate.title,
                description: toValidate.description || '',
                assignedTo: selectedUser.id,
                assignedToName: selectedUser.name,
                status: toValidate.status === 'todo' ? 'todo' : toValidate.status,
                priority: toValidate.priority === 'urgent' ? 'high' : toValidate.priority,
                dueDate: toValidate.due_date ? new Date(toValidate.due_date) : null,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                teamId: null,
                teamName: 'Individual Task',
                // Add folder and file info for reference
                folderId: folderId,
                fileId: fileId,
                orgTaskId: newId // Link to the org task
              };
              
              const mainTaskRef = await addDoc(collection(db, 'tasks'), mainTaskData);
              console.log('✅ TaskForm: Created main task for notifications:', mainTaskRef.id);
            } catch (error) {
              console.error('❌ TaskForm: Error creating main task for notifications:', error);
              // Don't fail the main task creation if notification task fails
            }
          }
        }
        
        onCreated && onCreated(newId);
      }
      
      // Send WhatsApp message if enabled (only for new tasks or if assigned user changed)
      if (sendWhatsApp && toValidate.assigned_to && toValidate.due_date && (!taskToEdit || taskToEdit.assignedTo !== toValidate.assigned_to)) {
        await sendWhatsAppMessage(toValidate.title, toValidate.assigned_to, toValidate.due_date);
      }
      
      // Reset form only if creating new task
      if (!taskToEdit) {
        setForm({
          task_id: '', title: '', description: '', assigned_to: '', status: 'todo', priority: 'medium',
          due_date: '', start_date: '', completion_date: '', attachments: [], comments: [], dependencies: []
        });
        setDepsText('');
      }
    } catch (e: any) {
      onError && onError(e?.message || `Failed to ${taskToEdit ? 'update' : 'create'} task`);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input className="px-3 py-2 border rounded-lg" placeholder="Task ID (auto if blank)" value={form.task_id} onChange={e => update('task_id', e.target.value)} />
        <input className="px-3 py-2 border rounded-lg" placeholder="Particulars" value={form.title} onChange={e => update('title', e.target.value)} />
        <select className="px-3 py-2 border rounded-lg" value={form.assigned_to || ''} onChange={e => update('assigned_to', e.target.value)}>
          <option value="">Select User</option>
          {users.map(user => (
            <option key={user.id} value={user.name}>
              {user.name} ({user.email}) {user.phoneNumber ? '📱' : '❌'}
            </option>
          ))}
        </select>
        <select className="px-3 py-2 border rounded-lg" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
          <option value="">Select Category</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>
              {cat === 'magazine' ? 'Magazine' : 
               cat === 'event' ? 'Event' : 
               cat === 'project' ? 'Project' : 
               cat === 'other' ? 'Other' : cat}
            </option>
          ))}
        </select>
        <select className="px-3 py-2 border rounded-lg" value={form.status} onChange={e => update('status', e.target.value as TaskStatus)}>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="px-3 py-2 border rounded-lg" value={form.priority} onChange={e => update('priority', e.target.value as TaskPriority)}>
          {priorities.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <DatePicker
          selected={dueDate}
          onChange={handleDueDateChange}
          dateFormat="yyyy-MM-dd"
          placeholderText="Due Date"
          className="px-3 py-2 border rounded-lg w-full"
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
        />
        <DatePicker
          selected={startDate}
          onChange={handleStartDateChange}
          dateFormat="yyyy-MM-dd"
          placeholderText="Start Date"
          className="px-3 py-2 border rounded-lg w-full"
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
        />
        <DatePicker
          selected={completionDate}
          onChange={handleCompletionDateChange}
          dateFormat="yyyy-MM-dd"
          placeholderText="Completion Date"
          className="px-3 py-2 border rounded-lg w-full"
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          minDate={startDate || undefined}
        />
      </div>
      <textarea className="w-full px-3 py-2 border rounded-lg" placeholder="Description" value={form.description || ''} onChange={e => update('description', e.target.value)} />
      <input className="w-full px-3 py-2 border rounded-lg" placeholder="Dependencies (comma separated task_ids)" value={depsText} onChange={e => setDepsText(e.target.value)} />
      
      {/* WhatsApp Notification Section */}
      {/* <div className="bg-green-50 p-4 rounded-lg border">
        <h4 className="text-sm font-medium text-gray-700 mb-3">📱 WhatsApp Notification</h4>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="sendWhatsApp"
              checked={sendWhatsApp}
              onChange={e => setSendWhatsApp(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="sendWhatsApp" className="text-sm text-gray-700">
              Send WhatsApp notification to assigned user automatically
            </label>
          </div>
          <p className="text-xs text-gray-600">
            ✅ Uses the same template system as Admin Dashboard - phone numbers retrieved from user profiles
          </p>
        </div>
      </div> */}
      <div className="space-y-4">
        {/* Upload Options */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            <label className="px-3 py-2 border rounded-lg cursor-pointer">
              {uploading ? 'Uploading...' : 'Single Upload'}
              <input type="file" className="hidden" onChange={handleSingleUpload} />
            </label>
            <button
              onClick={() => setShowBulkUpload(!showBulkUpload)}
              className="px-3 py-2 border rounded-lg hover:bg-gray-50"
            >
              {showBulkUpload ? 'Hide Bulk Upload' : 'Bulk Upload'}
            </button>
          </div>
          <button className="px-3 py-2 border rounded-lg" onClick={onDownload}>Download All</button>
        </div>

        {/* Bulk Upload Component */}
        {showBulkUpload && (
          <BulkUpload
            pathPrefix={`org/${folderId}/${fileId}/attachments`}
            onFilesUploaded={handleBulkUpload}
            onError={onError}
            maxFiles={20}
            maxFileSize={100}
          />
        )}

        {/* Attached Files */}
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700">Attached Files ({attachedFiles.length})</h4>
          {attachedFiles.map(file => (
            <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">
                  {file.type.startsWith('image/') ? '🖼️' : 
                   file.type === 'application/pdf' ? '📄' :
                   file.type.startsWith('text/') ? '📝' : '📎'}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB • {new Date(file.uploadedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => file.url && setViewingDocument({url: file.url, name: file.name})}
                  className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                >
                  View
                </button>
                {file.url && (
                  <a
                    href={file.url}
                    download={file.name}
                    className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                  >
                    Download
                  </a>
                )}
                <button
                  onClick={async () => {
                    try {
                      await fileStorageService.deleteFile(file);
                      setAttachedFiles(prev => prev.filter(f => f.id !== file.id));
                    } catch (error) {
                      onError && onError('Failed to delete file');
                    }
                  }}
                  className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-end">
        <button className="px-3 py-2 bg-blue-600 text-white rounded-lg" onClick={handleSubmit}>Create Task</button>
      </div>
      
      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewer
          documentUrl={viewingDocument.url}
          fileName={viewingDocument.name}
          onClose={() => setViewingDocument(null)}
        />
      )}
    </div>
  );
};

export default TaskForm;
